'use client';

import type { FormSchema } from '~/landing/core/simulator/future-savings-planner/schema';

import {
  Badge,
  FieldWrapper,
  FormField,
  Icon,
  Select,
  SimpleCard,
  TextInput,
} from '@polyseme/design-system';
import { formatCurrency as _formatCurrency } from '@polyseme/utils/number/number-utils';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { computeTaxSavingResults } from './calculations';
import {
  shareHouseholdOptions,
  taxSavingSimulatorConstants,
} from './constants';

const formatCurrency = (value: number | null | undefined) => {
  const DECIMAL_PLACES = 6;
  const isCompact = (value?.toFixed(0).length || 0) > DECIMAL_PLACES;
  return _formatCurrency(value, { isCompact });
};

export const TaxSavingsSimulator = () => {
  const t = useTranslations('landing.retraite');
  const form = useFormContext<FormSchema>();

  const now = new Date();
  const nextYear = now.getFullYear() + 1;
  // Months left before the new year, e.g. 5 in July (11 - 6).
  const monthsUntilYearEnd = 11 - now.getMonth();

  const [
    householdAnnualIncome,
    taxSharesHousehold,
    initialDeposit,
    monthlyPayment,
  ] = form.watch([
    'taxSavingsSimulator.householdAnnualIncome',
    'taxSavingsSimulator.taxSharesHousehold',
    'futureSavingsPlanner.initialDeposit',
    'futureSavingsPlanner.monthlyPayment',
  ]);

  // The deductible deposit for this year = initial deposit + the recurring
  // payments still to be made before year-end (vi + vr * (nbMois - 1)).
  const deposit =
    (initialDeposit ?? 0) +
    (monthlyPayment ?? 0) * Math.max(0, monthsUntilYearEnd - 1);

  const { tmi, taxSavings, taxWithoutPer, taxWithPer } = useMemo(
    () =>
      computeTaxSavingResults({
        householdAnnualIncome,
        taxSharesHousehold,
        deposit,
      }),
    [householdAnnualIncome, taxSharesHousehold, deposit]
  );

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <SimpleCard
        className="col-span-1"
        title={t('taxSavingsSimulator.form.title')}
      >
        <div className="flex flex-col gap-6">
          <FormField
            control={form.control}
            name="taxSavingsSimulator.householdAnnualIncome"
            render={({ field }) => (
              <FieldWrapper
                description={t(
                  `taxSavingsSimulator.form.householdAnnualIncome.helper`
                )}
                label={t(
                  `taxSavingsSimulator.form.householdAnnualIncome.label`
                )}
              >
                <TextInput
                  {...field}
                  className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  inputMode="decimal"
                  rightIcon="euro"
                  type="number"
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (isNaN(value)) {
                      field.onChange('');
                      return;
                    }

                    const nonNegativeValue = Math.max(0, value);

                    const clampedValue = Math.min(
                      taxSavingSimulatorConstants.householdAnnualIncome.max,
                      nonNegativeValue
                    );

                    field.onChange(clampedValue);
                  }}
                />
              </FieldWrapper>
            )}
          />
          <FormField
            control={form.control}
            name="taxSavingsSimulator.taxSharesHousehold"
            render={({ field }) => (
              <FieldWrapper
                description={t(
                  'taxSavingsSimulator.form.taxSharesHousehold.helper'
                )}
                label={t('taxSavingsSimulator.form.taxSharesHousehold.label')}
              >
                <Select
                  {...field}
                  aria-label={t(
                    'taxSavingsSimulator.form.taxSharesHousehold.label'
                  )}
                  options={shareHouseholdOptions.map(({ key, value }) => ({
                    label: t(
                      `taxSavingsSimulator.form.taxSharesHousehold.options.${key}`
                    ),
                    value: value,
                  }))}
                  value={field.value ? field.value.toString() : ''}
                />
              </FieldWrapper>
            )}
          />
        </div>
      </SimpleCard>

      <SimpleCard
        className="col-span-1 flex flex-col lg:col-span-2"
        title={t('taxSavingsSimulator.result.title', {
          nextYear: String(nextYear),
        })}
      >
        <div className="mb-4">
          <p className="text-sm text-gray-500">
            {t('taxSavingsSimulator.result.tmi-label')}
          </p>
          <p className="text-2xl font-semibold">{Math.round(tmi * 100)} %</p>
        </div>
        <div className="flex h-full items-center">
          <div
            aria-hidden="true"
            className="grid h-min w-full grid-cols-1 gap-6 xl:grid-cols-2"
          >
            <div className="flex h-full items-center justify-around rounded-lg border p-6">
              <div>
                <Badge variant="neutral">
                  {t('taxSavingsSimulator.result.without')}
                </Badge>
                <p className="mt-2 text-2xl font-semibold text-gray-500 md:text-3xl">
                  {formatCurrency(taxWithoutPer)}
                </p>
                <p className="text-sm">
                  {t('taxSavingsSimulator.result.of-tax')}
                </p>
              </div>
              <Icon className="size-6 translate-y-1.5" name="minus" />
              <div>
                <Badge icon="piggy-bank">
                  {t('taxSavingsSimulator.result.with')}
                </Badge>
                <p className="mt-2 text-2xl font-semibold md:text-3xl">
                  {formatCurrency(taxWithPer)}
                </p>
                <p className="text-sm">
                  {t('taxSavingsSimulator.result.of-tax')}
                </p>
              </div>
            </div>

            <div className="relative flex h-full items-center justify-around gap-6 rounded-lg border-2 border-[#89C1C7] shadow-[inset_0_0_0_4px_#E9F5F7]">
              <div className="flex size-full items-center justify-around gap-6 rounded-lg p-6">
                <Icon className="size-6 translate-y-1.5" name="equal" />
                <div>
                  <p className="mt-2 text-2xl font-semibold md:text-3xl">
                    {formatCurrency(taxSavings)}
                  </p>
                  <p className="text-sm">
                    {t('taxSavingsSimulator.result.tax-reduction-info', {
                      nextYear: String(nextYear),
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <span className="sr-only">
          {t('taxSavingsSimulator.result.summary', {
            taxWithout: formatCurrency(taxWithoutPer),
            taxWith: formatCurrency(taxWithPer),
            taxSavings: formatCurrency(taxSavings),
          })}
        </span>
      </SimpleCard>
    </div>
  );
};
